import React, { useEffect, useState, useRef } from 'react';
import { Spin, Button, Table } from 'antd';
import { useParams } from 'react-router-dom';
import intl from 'react-intl-universal';
import { getPeripheralListByResvNo } from '@/api/peripheral';
import { parseFen2Currency, isNil } from '@/utils/number';

import PeripheralReturnModal, { IModalRef } from '@/pages/goods/components/PeripheralReturnModal';
import useSettleDrawer from '@/pages/goods/peripheralRental/components/SettleDrawer';
import { getRenewalParams, IPreOrderDetailItem } from '@/pages/goods/peripheralRental/help';

import styles from './index.module.scss';

const Index = () => {
  const { resvNo } = useParams<any>();

  const [loading, setLoading] = useState<boolean>(false);
  const [list, setList] = useState<any[]>([]);

  // 表格勾选
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 外设归还
  const returnRef = useRef<IModalRef>(null);
  // 结算抽屉
  const settleDrawer = useSettleDrawer();

  // 查询列表
  const getTableData = async () => {
    setLoading(true);
    try {
      const res: any = await getPeripheralListByResvNo({ resvNo });
      setList(res?.data || []);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTableData();
  }, []);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const returnPeripheral = (type: 'single' | 'batch', record?: any) => {
    if (type === 'single') {
      const returns = [
        {
          spuName: record?.spuName,
          skuCode: record?.skuCode,
          roomId: record?.roomId,
          totalTimes: record?.totalTimes,
          roomQty: record?.qty,
          qty: 0, // 归还数量
        },
      ];
      returnRef.current.openModal({
        resvNo,
        returns,
      });
      return;
    }

    // 批量归还
    const arr = list.filter((item) => selectedRowKeys.includes(item.skuCode));
    const returns = (arr || []).map((item) => {
      return {
        spuName: item?.spuName,
        skuCode: item?.skuCode,
        roomId: item?.roomId,
        totalTimes: item?.totalTimes,
        roomQty: item?.qty,
        qty: 0, // 归还数量
      };
    });
    returnRef.current.openModal({
      resvNo,
      returns,
    });
  };

  // 续租
  const renew = async (details: IPreOrderDetailItem[], roomId: number, roomNo: string) => {
    setLoading(true);
    try {
      const { orderNo = '', homeowner } = await getRenewalParams({
        details,
        leaseType: 2,
        rest: {
          roomId,
          roomNo,
        },
      });

      setLoading(false);
      // 优化没有订单号生成 不打开续租抽屉
      if (isNil(orderNo)) return;

      settleDrawer.showDrawer({
        orderNo,
        leaseType: 2, // 续租
        homeowner: JSON.stringify(homeowner),
        callback: () => {
          setSelectedRowKeys([]);
          getTableData();
        },
      });
    } catch (error) {
      setLoading(false);
    }
  };

  // 单个续租
  const singleRenew = (record) => {
    const details = [
      {
        saleCount: record?.qty || 0,
        saleTimeCount: 1,
        skuCode: record?.skuCode,
      },
    ];
    renew(details, record?.roomId, record?.roomNo);
  };

  // 批量续租
  const batchRenew = () => {
    let roomId = null;
    let roomNo = null;
    const arr = list.filter((item) => selectedRowKeys.includes(item.skuCode));
    const details = (arr || []).map((item) => {
      roomId = item?.roomId;
      roomNo = item?.roomNo;
      return {
        saleCount: item?.qty || 0,
        saleTimeCount: 1,
        skuCode: item?.skuCode,
      };
    });

    renew(details, roomId, roomNo);
  };

  const columns: any = [
    {
      title: '房号',
      dataIndex: 'roomNo',
      key: 'roomNo',
    },
    {
      title: '外设SPUID',
      dataIndex: 'spuId',
      key: 'spuId',
    },
    {
      title: '外设名称',
      dataIndex: 'spuName',
      key: 'spuName',
    },
    {
      title: '总租借次数',
      dataIndex: 'totalTimes',
      key: 'totalTimes',
      align: 'center',
    },
    {
      title: '房间库存',
      dataIndex: 'qty',
      key: 'qty',
      align: 'center',
    },
    {
      title: '总租金',
      dataIndex: 'totalAmt',
      key: 'totalAmt',
      align: 'right',
      render: (val) => {
        return !isNil(val) ? parseFen2Currency(val) : '';
      },
    },
    {
      title: '操作',
      dataIndex: 'operate',
      align: 'center',
      render: (_val, record) => {
        return (
          <>
            <Button type="link" onClick={() => singleRenew(record)}>
              续租
            </Button>
            <Button type="link" onClick={() => returnPeripheral('single', record)}>
              归还
            </Button>
          </>
        );
      },
    },
  ];

  const disabled = selectedRowKeys.length === 0;

  return (
    <div className={styles.pageContainer}>
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            style={{ marginRight: '22px' }}
            disabled={disabled}
            onClick={batchRenew}
          >
            一键续租
          </Button>
          <Button type="primary" disabled={disabled} onClick={() => returnPeripheral('batch')}>
            一键归还
          </Button>
        </div>
        <div className={styles.tableWrapper}>
          <Table
            rowKey="skuCode"
            loading={loading}
            pagination={false}
            rowSelection={rowSelection}
            dataSource={list || []}
            columns={columns || []}
            scroll={{ x: 800 }}
          />
        </div>
      </Spin>
      <PeripheralReturnModal
        ref={returnRef}
        callback={() => {
          setSelectedRowKeys([]);
          getTableData();
        }}
      />
    </div>
  );
};

export default Index;
